import { fullLabel } from "../WhereItIs";
import { useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Printer, Search } from "lucide-react";

// THE HIDDEN POWER, BROUGHT ONTO THE PAGE HE IS STANDING ON: print every label beneath one place
// in a single run. It used to live on a separate screen whose only job was printing.
export const ScopePrintDialog = ({ open, nodes, onClose, onPrint }) => {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const ql = q.trim().toLowerCase();
  const list = useMemo(() => (nodes || [])
    .filter((n) => !ql || `${n.short_code} ${fullLabel(n) || n.name}`.toLowerCase().includes(ql))
    .slice(0, 60), [nodes, ql]);

  const go = async (n) => {
    setBusy(n.id);
    try {
      const { data } = await api.get(`/locations/${n.id}/position-labels`);
      if (!data.labels.length) { toast.error(`Nothing to print under ${n.short_code} — it has no shelves or spots yet.`); return; }
      onClose();
      onPrint(data.labels, `Everything under ${data.parent_code || n.short_code}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="scope-print-dialog" className="rounded-xl border-slate-200 sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Print everything under one place</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Every shelf, level, bay and spot beneath the place you pick, in one paced run. Only what actually comes out of the printer is recorded as printed.</p>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="scope-print-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search a site, sector or rack…" className="rounded-lg border-slate-200 pl-9 h-11" />
        </div>
        <div className="max-h-[46dvh] overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
          {list.length === 0 && <div className="p-6 text-center font-mono text-xs text-slate-400">NO PLACES MATCH</div>}
          {list.map((n) => (
            <div key={n.id} data-testid={`scope-print-row-${n.short_code}`} className="flex items-center gap-2 px-3 py-2 min-h-[48px]">
              <span className="font-mono text-xs shrink-0">{n.short_code}</span>
              <span className="flex-1 break-words text-sm text-slate-600">{fullLabel(n) || n.name}</span>
              <Button data-testid={`scope-print-go-${n.short_code}`} size="sm" disabled={busy === n.id}
                className="rounded-lg h-9 bg-[#1F1F1F] hover:bg-black text-white shrink-0" onClick={() => go(n)}>
                {busy === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Printer className="w-3.5 h-3.5 mr-1" />Print</>}
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-lg border-slate-200 h-11" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
